import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import VoiceRecorder from "@/components/VoiceRecorder";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Trash2, Footprints, Wind, Bike, Waves, Flame, Droplet } from "lucide-react";
import { toast } from "sonner";

const ACTIVITY_META = {
  walking: { icon: Footprints, label: "Walk" },
  running: { icon: Wind, label: "Run" },
  cycling: { icon: Bike, label: "Cycle" },
  swimming: { icon: Waves, label: "Swim" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/foods/today");
    setData(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/foods/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  const goal = data?.daily_sugar_goal || 30;
  const total = data?.total_sugar_grams || 0;
  const pct = Math.min(100, (total / goal) * 100);
  const over = total > goal;
  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <Layout>
      <div className="space-y-8">
        <div className="fade-up">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
            {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black mt-1">Hey {firstName} 👋</h1>
        </div>

        {/* Voice hero */}
        <div className="bg-card border border-border rounded-2xl p-8 sm:p-10 flex flex-col items-center fade-up">
          <VoiceRecorder onLogged={load} onUpgrade={() => navigate("/pricing")} />
        </div>

        {/* Daily progress */}
        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 fade-up">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Sugar today</p>
              <p className="font-heading text-4xl font-black mt-1" data-testid="today-total-sugar">
                {total}<span className="text-xl text-muted-foreground font-bold">g</span>
              </p>
            </div>
            <p className={`text-sm font-bold ${over ? "text-destructive" : "text-secondary"}`} data-testid="sugar-goal-label">
              {over ? `${(total - goal).toFixed(1)}g over goal` : `${(goal - total).toFixed(1)}g left`}
            </p>
          </div>
          <Progress value={pct} className={`h-3 ${over ? "[&>div]:bg-destructive" : "[&>div]:bg-secondary"}`} />
          <p className="text-xs text-muted-foreground mt-2">Goal: {goal}g · {data?.total_calories || 0} kcal from sugar</p>
        </div>

        {/* Burn it off */}
        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 fade-up" data-testid="burn-off-section">
          <div className="flex items-center gap-2 mb-1">
            <Flame className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-xl font-bold">Burn it off</h2>
          </div>
          <p className="text-muted-foreground text-sm mb-5">Minutes to burn today's {data?.total_calories || 0} kcal, tuned to you.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {Object.entries(ACTIVITY_META).map(([key, meta]) => {
              const Icon = meta.icon;
              const mins = data?.total_burn_off?.[key] ?? 0;
              return (
                <div key={key} className="bg-muted rounded-xl p-4 flex flex-col items-center text-center" data-testid={`burn-off-${key}`}>
                  <Icon className="w-6 h-6 text-secondary mb-2" />
                  <p className="font-heading text-2xl font-black">{mins}</p>
                  <p className="text-xs text-muted-foreground font-semibold">min {meta.label}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Log list */}
        <div className="fade-up">
          <h2 className="font-heading text-xl font-bold mb-4">Today's log</h2>
          {data && data.logs.length === 0 ? (
            <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center text-muted-foreground" data-testid="empty-log">
              <Droplet className="w-8 h-8 mx-auto mb-3 text-secondary" />
              Nothing logged yet. Tap the mic and tell me what you had.
            </div>
          ) : (
            <div className="space-y-3">
              {data?.logs.map((log) => (
                <div key={log.id} className="bg-card border border-border rounded-2xl p-5 flex items-start justify-between gap-4" data-testid={`log-item-${log.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold capitalize truncate">{log.description}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-primary/10 text-primary">{log.sugar_grams}g sugar</span>
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{log.calories} kcal</span>
                    </div>
                  </div>
                  <button onClick={() => remove(log.id)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0" data-testid={`delete-log-${log.id}`}>
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
